import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  BULLETIN_GRID_COLUMNS,
  BULLETIN_DETAIL_FIELDS,
} from '../../constants/personBulletinFormConstants';
import { useBulletinDialog } from '../../hooks/useBulletinDialog';
import { getVehicleBulletins, deleteBulletin } from '../../services/bulletinService';
import LocalPrintshopIcon from '@material-ui/icons/LocalPrintshop';
import { buildSOPDetailPrintHTML } from '../../utility/addressSOPHelper';

const VehicleBulletinsTab = ({ ptsVehicleID, canEdit = true }) => {
  const dispatch = useDispatch();
  const bulletinPermission = usePermissions(PERMISSION_RESOURCES.ALERT);
  const [loading, setLoading] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const { openBulletinDialog } = useBulletinDialog();
  const [detailTarget, setDetailTarget] = useState(null);
  const printIframeRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadBulletins = useCallback(async () => {
    if (!ptsVehicleID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getVehicleBulletins(ptsVehicleID),
      onSuccess: (data) => setBulletins(data || []),
    });
  }, [dispatch, ptsVehicleID]);

  const handleSaved = useCallback(() => {
    loadBulletins();
  }, [loadBulletins]);

  const handleAdd = () =>
    openBulletinDialog({
      vehicleId: ptsVehicleID,
      canSave: canEdit && bulletinPermission.Create,
      onSaved: handleSaved,
    });

  const handleEdit = (row) =>
    openBulletinDialog({
      bulletinId: row.ptsAlertID,
      vehicleId: ptsVehicleID,
      canSave: canEdit && bulletinPermission.Edit,
      onSaved: handleSaved,
    });

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        'Are you sure you want to delete this bulletin?',
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteBulletin(row.ptsAlertID),
            onSuccess: () => {
              dispatch(notify('Bulletin deleted successfully', 'success'));
              loadBulletins();
            },
          });
        },
        'Delete Bulletin'
      );
    },
    [requestConfirm, dispatch, loadBulletins]
  );

  const handlePrint = useCallback(() => {
    if (!detailTarget) return;

    const html = buildSOPDetailPrintHTML(
      detailTarget,
      BULLETIN_DETAIL_FIELDS,
      detailTarget.AlertType || 'Bulletin Details'
    );
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [detailTarget]);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  return (
    <Box className="vehicle-grid-tab">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Bulletin"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEdit || !bulletinPermission.Create}
        />
      </Box>

      <CommonDataGrid
        rows={bulletins}
        columns={BULLETIN_GRID_COLUMNS}
        getRowId={(row) => row.ptsAlertID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Vehicle Bulletins' })}
        actions={{
          edit: {
            visible: true,
            onClick: handleEdit,
            disabled: !canEdit || !bulletinPermission.Edit,
          },
          delete: {
            visible: true,
            onClick: handleDelete,
            disabled: !canEdit || !bulletinPermission.Delete,
          },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={detailTarget.AlertType || 'Bulletin Details'}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}
          customButtons={
            <RMSButton
              label="Print"
              variant="contained"
              color="primary"
              prefixIcon={<LocalPrintshopIcon />}
              onClick={handlePrint}
            />
          }>
          <Grid container spacing={1}>
            {BULLETIN_DETAIL_FIELDS.map(({ field, label, render }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {render ? render(detailTarget[field]) : detailTarget[field] || ''}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <iframe
            ref={printIframeRef}
            title="vehicle-bulletin-print-frame"
            className="vehicle-bulletin-print-iframe"
          />
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default VehicleBulletinsTab;
